/**
 * Centralized helpers for the silent-refresh dance.
 *
 *  - `bootRefresh()` runs at cold load. On success it stores the bearer; on
 *    failure it returns false and the caller redirects.
 *  - `redirectToCentralizedLogin()` stashes the deep link in sessionStorage
 *    (the ONLY thing allowed in sessionStorage) and `window.location.assign`s
 *    to the centralized-login URL with the `returnUrl` query.
 */
import { authApi } from '@/api/auth';
import { ApiError } from '@/api/errors';
import { setAccessToken, setCsrfToken } from './authStore';
import { getBootstrap } from '@/config/bootstrap';

const DEEP_LINK_KEY = 'wsr_product_admin.deepLink';

export async function bootRefresh(): Promise<boolean> {
  try {
    const body = await authApi.refresh();
    if (body.accessToken) {
      setAccessToken(body.accessToken, body.expiresAt ?? null);
      // Retain the optional CSRF token from the flat response body in the
      // in-memory auth state; wsr-api requests do not consume it.
      setCsrfToken(body.csrfToken ?? null);
      return true;
    }
    return false;
  } catch (err) {
    if (err instanceof ApiError) {
      // 401 / 403 / network are all "not authed" for boot.
      return false;
    }
    return false;
  }
}

export function stashDeepLink(path: string): void {
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function popDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return v;
  } catch {
    return null;
  }
}

export function clearDeepLink(): void {
  try {
    sessionStorage.removeItem(DEEP_LINK_KEY);
  } catch {
    /* ignore */
  }
}

export function centralizedLoginUrl(returnUrl: string): string {
  const { loginUrl } = getBootstrap();
  const sep = loginUrl.includes('?') ? '&' : '?';
  return `${loginUrl}${sep}returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function redirectToCentralizedLogin(currentPath: string): void {
  stashDeepLink(currentPath);
  const returnUrl = `${location.origin}/auth/return`;
  if (typeof window !== 'undefined') {
    window.location.assign(centralizedLoginUrl(returnUrl));
  }
}
